import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { AlertTriangle, Download, LoaderCircle, RefreshCw, ShieldAlert } from "lucide-react";

import { fetchAppUpdateStatus } from "@/api/system";
import { BUILD_META } from "@/buildMeta.generated";
import { PlayInAppUpdate } from "@/lib/playInAppUpdates";
import { getNativePlatform, isNativeApp } from "@/lib/runtime";
import "@/styles/native-app-update.css";

const OPTIONAL_REMINDER_DELAY_MS = 24 * 60 * 60 * 1000;
const DISMISS_KEY = "lapstr:native-app-update-dismissal:v2";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=se.lapstr.app";

const NativeAppUpdateContext = createContext({
  status: null,
  update: null,
  showUpdate: () => {},
  refresh: () => Promise.resolve(),
});

function readDismissal() {
  try {
    return JSON.parse(localStorage.getItem(DISMISS_KEY) || "null");
  } catch {
    return null;
  }
}

function shouldShowOptional(update) {
  const dismissal = readDismissal();
  if (!dismissal || Number(dismissal.availableBuild) !== Number(update?.available_build)) return true;
  return Date.now() - Number(dismissal.dismissedAt || 0) >= OPTIONAL_REMINDER_DELAY_MS;
}

export function NativeAppUpdateProvider({ children }) {
  const native = isNativeApp();
  const platform = getNativePlatform();
  const supported = native && platform === "android" && BUILD_META.stage === "prod";
  const [update, setUpdate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const dialogRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!supported) return;

    try {
      const [appInfo, playInfo] = await Promise.all([
        CapacitorApp.getInfo(),
        PlayInAppUpdate.getUpdateInfo(),
      ]);
      const playHasUpdate = Boolean(playInfo?.available) || playInfo?.installStatus === "downloaded";
      if (!playHasUpdate) {
        setUpdate(null);
        setDialogOpen(false);
        setDownloadProgress(null);
        return;
      }

      const policy = await fetchAppUpdateStatus({
        platform,
        build: Number(appInfo.build),
        version: appInfo.version,
        channel: "production",
      });
      if (!policy || policy.status === "up_to_date") {
        setUpdate(null);
        setDialogOpen(false);
        return;
      }

      const merged = {
        ...policy,
        current_build: Number(appInfo.build),
        available_build: Number(playInfo.availableVersionCode || 0),
        play: playInfo,
        downloaded: playInfo.installStatus === "downloaded",
      };
      setUpdate(merged);
      if (merged.downloaded || merged.status === "required") {
        setDialogOpen(true);
      } else {
        setDialogOpen(shouldShowOptional(merged));
      }
    } catch (error) {
      console.warn("[APP UPDATE] Play Core-kontrollen misslyckades", error);
      // Kontrollfel eller sideloadade APK-filer får aldrig låsa användaren ute.
      setUpdate(null);
      setDialogOpen(false);
    }
  }, [platform, supported]);

  useEffect(() => {
    if (!supported) return undefined;
    let disposed = false;
    let appStateHandle;
    let stateHandle;
    let flowHandle;
    let refreshTimer;

    const register = async () => {
      stateHandle = await PlayInAppUpdate.addListener("updateStateChanged", (state) => {
        if (disposed) return;
        if (state.installStatus === "pending") {
          setDownloadProgress(0);
          setDialogOpen(true);
        } else if (state.installStatus === "downloading") {
          const total = Number(state.totalBytesToDownload || 0);
          const downloaded = Number(state.bytesDownloaded || 0);
          setDownloadProgress(total > 0 ? Math.min(100, Math.round((downloaded / total) * 100)) : 0);
          setDialogOpen(true);
        } else if (state.installStatus === "downloaded") {
          setDownloadProgress(100);
          setUpdate((current) => current ? { ...current, downloaded: true } : current);
          setDialogOpen(true);
        } else if (state.installStatus === "installed") {
          setUpdate(null);
          setDialogOpen(false);
        } else if (state.installStatus === "failed" || state.installStatus === "cancelled") {
          console.warn(`[APP UPDATE] Google Play avbröt hämtningen (${state.installStatus})`, state.installErrorCode);
          setDownloadProgress(null);
          setDialogOpen(true);
        }
      });
      flowHandle = await PlayInAppUpdate.addListener("updateFlowResult", ({ cancelled }) => {
        if (!disposed && cancelled) setDialogOpen(true);
      });
      appStateHandle = await CapacitorApp.addListener("appStateChange", ({ isActive }) => {
        if (isActive && !disposed) void refresh();
      });
      await refresh();
      refreshTimer = window.setInterval(() => {
        if (!disposed && document.visibilityState === "visible") void refresh();
      }, 15 * 60 * 1000);
      if (disposed) {
        await Promise.all([stateHandle?.remove(), flowHandle?.remove(), appStateHandle?.remove()]);
      }
    };

    void register();
    return () => {
      disposed = true;
      window.clearInterval(refreshTimer);
      void stateHandle?.remove();
      void flowHandle?.remove();
      void appStateHandle?.remove();
    };
  }, [refresh, supported]);

  const startOrCompleteUpdate = async () => {
    if (!update || starting) return;
    setStarting(true);
    try {
      if (update.downloaded) {
        await PlayInAppUpdate.completeUpdate();
        return;
      }

      const preferred = update.status === "required" ? "immediate" : (update.update_type || "flexible");
      const immediateAllowed = Boolean(update.play?.immediateAllowed);
      const flexibleAllowed = Boolean(update.play?.flexibleAllowed);
      const type = preferred === "immediate" && immediateAllowed
        ? "immediate"
        : flexibleAllowed
          ? "flexible"
          : immediateAllowed
            ? "immediate"
            : null;

      if (!type) throw new Error("Google Play tillåter inget uppdateringsflöde för den här versionen.");
      const result = await PlayInAppUpdate.startUpdate({ type });
      if (result?.started && type === "flexible") {
        setDownloadProgress((current) => current ?? 0);
        setDialogOpen(true);
      }
    } catch (error) {
      console.warn("[APP UPDATE] In-app-uppdateringen kunde inte startas", error);
      await Browser.open({ url: update.store_url || PLAY_STORE_URL, toolbarColor: "#121D20" });
    } finally {
      setStarting(false);
    }
  };

  const remindLater = useCallback(() => {
    if (update?.status !== "optional" || update?.downloaded) return;
    localStorage.setItem(DISMISS_KEY, JSON.stringify({
      availableBuild: update.available_build,
      dismissedAt: Date.now(),
    }));
    setDialogOpen(false);
  }, [update]);

  const showUpdate = () => {
    if (update?.status === "optional" || update?.status === "required") setDialogOpen(true);
  };

  const required = update?.status === "required";
  const belowMinimum = required
    && Number(update?.current_build || 0) < Number(update?.minimum_supported_build || 1);
  const downloaded = Boolean(update?.downloaded);
  const downloading = downloadProgress !== null && !downloaded;
  const visible = supported && dialogOpen && (required || update?.status === "optional");

  useEffect(() => {
    if (!visible) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.requestAnimationFrame(() => dialogRef.current?.querySelector("button")?.focus());
    const handleEscape = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      if (!required && !downloaded) remindLater();
    };
    document.addEventListener("keydown", handleEscape, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape, true);
    };
  }, [downloaded, remindLater, required, visible]);

  return (
    <NativeAppUpdateContext.Provider value={{ status: update?.status || null, update, showUpdate, refresh }}>
      {children}
      {visible ? (
        <div className="native-app-update-overlay" role="presentation">
          <section
            ref={dialogRef}
            className="native-app-update-dialog"
            role="alertdialog"
            data-state="open"
            aria-modal="true"
            aria-labelledby="native-app-update-title"
            aria-describedby="native-app-update-message"
            data-required={required ? "true" : "false"}
            data-downloading={downloading ? "true" : "false"}
          >
            <span className="native-app-update-icon" aria-hidden="true">
              {downloaded ? <RefreshCw /> : downloading ? <LoaderCircle className="native-app-update-spinner" /> : required ? <ShieldAlert /> : <Download />}
            </span>
            <p className="native-app-update-kicker">
              {downloaded ? "Redo att installeras" : downloading ? "Laddar ned" : required ? "Uppdatering krävs" : "Ny version tillgänglig"}
            </p>
            <h2 id="native-app-update-title">
              {downloaded ? "Uppdateringen är redo" : downloading ? "Hämtar uppdateringen" : update.title || "Ny version av Lapstr"}
            </h2>
            <p id="native-app-update-message">
              {downloaded
                ? "Starta om Lapstr för att slutföra installationen."
                : downloading
                  ? "Vänta medan Google Play hämtar den nya versionen."
                : update.message || "Uppdatera Lapstr för att få den senaste versionen."}
            </p>
            {update.release_notes && !downloading ? (
              <div className="native-app-update-notes">
                <strong>Nyheter</strong>
                <p>{update.release_notes}</p>
              </div>
            ) : null}
            {required && !downloaded && !downloading ? (
              <p className="native-app-update-required-note">
                <AlertTriangle aria-hidden="true" />
                {belowMinimum
                  ? "Den installerade versionen stöds inte längre."
                  : "Den här uppdateringen behöver installeras innan du fortsätter."}
              </p>
            ) : null}
            {downloading ? (
              <div
                className="native-app-update-progress"
                role="progressbar"
                aria-label="Laddar ned uppdateringen"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={downloadProgress}
              >
                <span className="native-app-update-progress-copy">
                  <strong>Laddar ned</strong>
                  <span>{downloadProgress}%</span>
                </span>
                <span className="native-app-update-progress-track" aria-hidden="true">
                  <span style={{ width: `${Math.max(3, downloadProgress)}%` }} />
                </span>
              </div>
            ) : null}
            {!downloading ? (
              <div className="native-app-update-actions">
                <button type="button" className="native-app-update-primary" disabled={starting} onClick={startOrCompleteUpdate}>
                  {starting ? "Startar…" : downloaded ? "Installera och starta om" : "Uppdatera nu"}
                </button>
                {!required && !downloaded ? <button type="button" onClick={remindLater}>Senare</button> : null}
              </div>
            ) : null}
          </section>
        </div>
      ) : null}
    </NativeAppUpdateContext.Provider>
  );
}

export function useNativeAppUpdate() {
  return useContext(NativeAppUpdateContext);
}
